/**
 * CLE-2842 — the Adjustment Rules list shows one row per rule *bucket* instead of one per payer.
 *
 * Ani copies one rule to many payers, and each copy is its own row; the backend tags rows that are
 * the same rule for the same provider and differ only in payer with a shared `groupKey`. Only the
 * three types with a group-edit endpoint are collapsed. Everything else — other types, and rows
 * whose `groupKey` is null (Typical / Fallback) — is passed through untouched.
 */
export const GROUPABLE_RULE_TYPES = ["MPPR", "Case Rate", "Package"];

export interface IGroupableRuleRow {
  id: string;
  ruleType: string;
  groupKey?: string | null;
  /** CLE-2842 step 2 — null on a provider's "All payers" row. */
  payerId?: number | string | null;
  payerName?: string;
  attachments?: { id: string }[];
  modifiedFields?: string[];
}

export interface IRuleBucketFields<TRow> {
  /**
   * Every row in the bucket — the All payers row (payerId null) first, then by payer name, then
   * id. The display row is `members[0]`.
   */
  members: TRow[];
  memberIds: string[];
  payerNames: string[];
}

export type TRuleBucketRow<TRow> = TRow & IRuleBucketFields<TRow>;

/** Every item of every list, once, in first-seen order. */
const unionBy = <TItem>(lists: TItem[][], keyOf: (item: TItem) => string) => {
  const seen = new Set<string>();
  return lists.flat().filter((item) => {
    const key = keyOf(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// CLE-2842 step 2 — a provider's All payers row (payerId explicitly null) leads its bucket, as
// it does in the server's `bucketMembers`. A row that simply carries no payerId is not one.
const allPayersFirst = (row: IGroupableRuleRow) =>
  row.payerId === null ? 0 : 1;

const byPayerNameThenId = (a: IGroupableRuleRow, b: IGroupableRuleRow) => {
  const byAllPayers = allPayersFirst(a) - allPayersFirst(b);
  if (byAllPayers !== 0) return byAllPayers;
  const byName = (a.payerName ?? "").localeCompare(b.payerName ?? "");
  if (byName !== 0) return byName;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
};

const bucketKeyOf = (row: IGroupableRuleRow): string | null =>
  GROUPABLE_RULE_TYPES.includes(row.ruleType) && row.groupKey
    ? row.groupKey
    : null;

export function isRuleBucketRow<TRow>(row: TRow): row is TRuleBucketRow<TRow> {
  return Array.isArray((row as Partial<IRuleBucketFields<TRow>>)?.members);
}

/**
 * Collapses rows sharing a `groupKey` into one display row: the first member (the All payers row,
 * else by payer name, then id) spread, plus `members` / `memberIds` / `payerNames`. `payerName` becomes the names joined
 * with ", " so the Payer column's text filter and sort work unchanged — a bucket matches a payer
 * search when any member does. A bucket keeps the position of its first row in the input.
 */
export function groupRuleRows<TRow extends IGroupableRuleRow>(
  rows: TRow[]
): Array<TRow | TRuleBucketRow<TRow>> {
  const membersByKey = new Map<string, TRow[]>();
  rows.forEach((row) => {
    const key = bucketKeyOf(row);
    if (!key) return;
    const members = membersByKey.get(key);
    if (members) members.push(row);
    else membersByKey.set(key, [row]);
  });

  const emitted = new Set<string>();
  const result: Array<TRow | TRuleBucketRow<TRow>> = [];
  rows.forEach((row) => {
    const key = bucketKeyOf(row);
    if (!key) {
      result.push(row);
      return;
    }
    if (emitted.has(key)) return;
    emitted.add(key);
    const members = [...(membersByKey.get(key) ?? [row])].sort(
      byPayerNameThenId
    );
    const payerNames = members.map((member) => member.payerName ?? "—");
    // Contract-language attachments and modified fields are per payer, so the bucket row shows
    // every member's — not just the first one's. A field no member carries stays absent.
    const perPayer: Partial<IGroupableRuleRow> = {};
    if (members.some((member) => Array.isArray(member.attachments))) {
      perPayer.attachments = unionBy(
        members.map((member) => member.attachments ?? []),
        (attachment) => attachment.id
      );
    }
    if (members.some((member) => Array.isArray(member.modifiedFields))) {
      perPayer.modifiedFields = unionBy(
        members.map((member) => member.modifiedFields ?? []),
        (field) => field
      );
    }
    result.push({
      ...members[0],
      ...perPayer,
      members,
      memberIds: members.map((member) => member.id),
      payerNames,
      payerName: payerNames.join(", "),
    });
  });
  return result;
}
